'use client';

import { ReactNode, useEffect, useLayoutEffect, useRef, useState, type MouseEvent, type PointerEvent } from 'react';
import { Box, ButtonBase, Modal } from '@mui/material';
import { EASE_SHEET, reducedMotion } from './motion';
import { haptic } from './haptics';

const HOLD_MS = 450;
const SLOP = 8;
const OPEN_MS = 340;
const CLOSE_MS = 220;
const PREVIEW_INSET = 8;
const MENU_GAP = 8;

export function useLongPress(onLongPress: (target: HTMLElement) => void) {
  const timer = useRef(0);
  const origin = useRef<{ id: number; x: number; y: number } | null>(null);
  const fired = useRef(false);
  const pointerType = useRef('');

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const cancel = () => {
    window.clearTimeout(timer.current);
    origin.current = null;
  };

  const fire = (target: HTMLElement) => {
    cancel();
    haptic();
    onLongPress(target);
  };

  return {
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      pointerType.current = event.pointerType;
      fired.current = false;
      if (event.pointerType === 'mouse' || !event.isPrimary) return;
      const target = event.currentTarget;
      origin.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        fired.current = true;
        fire(target);
      }, HOLD_MS);
    },
    onPointerMove: (event: PointerEvent<HTMLElement>) => {
      const start = origin.current;
      if (start && start.id === event.pointerId && Math.hypot(event.clientX - start.x, event.clientY - start.y) > SLOP) cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    // Android fires contextmenu on a long press as well as our timer, so only the first one opens the menu.
    onContextMenu: (event: MouseEvent<HTMLElement>) => {
      event.preventDefault();
      if (fired.current) return;
      if (pointerType.current !== 'mouse') fired.current = true;
      fire(event.currentTarget);
    },
    onClickCapture: (event: MouseEvent<HTMLElement>) => {
      if (!fired.current) return;
      fired.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
  };
}

export type RowMenuAction = { label: string; icon: ReactNode; onSelect: () => void; color?: string };

type Props = {
  anchor: DOMRect;
  preview: ReactNode;
  actions: RowMenuAction[];
  onOpenPreview: () => void;
  onClose: () => void;
};

export default function RowMenu({ anchor, preview, actions, onOpenPreview, onClose }: Props) {
  // The Modal's portal renders its children a commit after this component, so measure once the frame is actually there.
  const [frame, setFrame] = useState<HTMLDivElement | null>(null);
  const stack = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState<number | null>(null);
  const [shown, setShown] = useState(false);
  const closing = useRef(false);

  useLayoutEffect(() => {
    if (!frame) return;
    const bounds = frame.getBoundingClientRect();
    const height = stack.current?.offsetHeight ?? 0;
    setTop(Math.max(bounds.top, Math.min(anchor.top, bounds.bottom - height)));
  }, [anchor, frame]);

  useEffect(() => {
    if (top === null) return;
    const frameId = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frameId);
  }, [top]);

  const close = (then?: () => void) => {
    if (closing.current) return;
    closing.current = true;
    setShown(false);
    then?.();
    window.setTimeout(onClose, CLOSE_MS);
  };

  const placed = top ?? anchor.top;
  const lift = anchor.top - placed;
  const ms = shown ? OPEN_MS : CLOSE_MS;
  const motion = `transform ${ms}ms ${EASE_SHEET}, opacity ${ms}ms ease`;
  const menuWidth = Math.min(260, anchor.width - PREVIEW_INSET * 2);

  return (
    <Modal open onClose={() => close()} hideBackdrop disableRestoreFocus>
      <Box sx={{ position: 'fixed', inset: 0, WebkitTapHighlightColor: 'transparent' }}>
        <Box
          onClick={() => close()}
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(20,20,18,0.16)',
            backdropFilter: 'blur(18px) saturate(140%)',
            WebkitBackdropFilter: 'blur(18px) saturate(140%)',
            opacity: shown ? 1 : 0,
            transition: `opacity ${ms}ms ease`,
            ...reducedMotion,
          }}
        />
        <Box ref={setFrame} aria-hidden sx={{ position: 'absolute', left: 0, right: 0, top: 'calc(env(safe-area-inset-top) + 12px)', bottom: 'calc(env(safe-area-inset-bottom) + 12px)', pointerEvents: 'none' }} />
        <Box
          ref={stack}
          sx={{
            position: 'absolute',
            left: anchor.left,
            width: anchor.width,
            top: placed,
            visibility: top === null ? 'hidden' : 'visible',
            pointerEvents: 'none',
          }}
        >
          <ButtonBase
            aria-label="Open conversation"
            onClick={() => close(onOpenPreview)}
            sx={{
              display: 'block',
              width: `calc(100% - ${PREVIEW_INSET * 2}px)`,
              mx: `${PREVIEW_INSET}px`,
              textAlign: 'left',
              borderRadius: '14px',
              overflow: 'hidden',
              bgcolor: 'var(--surface)',
              pointerEvents: 'auto',
              boxShadow: shown ? '0 18px 40px -12px rgba(20,20,18,0.35), 0 0 0 0.5px rgba(20,20,18,0.08)' : 'none',
              transform: shown ? 'scale(1.02)' : `translate3d(0, ${lift}px, 0)`,
              transition: `${motion}, box-shadow ${ms}ms ease`,
              ...reducedMotion,
            }}
          >
            {preview}
          </ButtonBase>
          <Box
            role="menu"
            sx={{
              mt: `${MENU_GAP}px`,
              ml: `${PREVIEW_INSET}px`,
              width: menuWidth,
              borderRadius: '14px',
              overflow: 'hidden',
              bgcolor: 'color-mix(in srgb, var(--surface) 94%, transparent)',
              backdropFilter: 'blur(24px) saturate(180%)',
              WebkitBackdropFilter: 'blur(24px) saturate(180%)',
              boxShadow: '0 12px 32px -8px rgba(20,20,18,0.28), 0 0 0 0.5px rgba(20,20,18,0.1)',
              pointerEvents: 'auto',
              transformOrigin: 'top left',
              opacity: shown ? 1 : 0,
              transform: shown ? 'none' : `translate3d(0, ${lift}px, 0) scale(0.6)`,
              transition: motion,
              ...reducedMotion,
            }}
          >
            {actions.map((action, index) => (
              <ButtonBase
                key={action.label}
                role="menuitem"
                autoFocus={index === 0}
                onClick={() => close(action.onSelect)}
                sx={{
                  display: 'flex',
                  width: '100%',
                  height: 46,
                  px: 2,
                  justifyContent: 'space-between',
                  gap: 1.5,
                  fontSize: '1.0625rem',
                  letterSpacing: '-0.01em',
                  color: action.color ?? 'var(--ink)',
                  boxShadow: index ? 'inset 0 0.5px 0 rgba(20,20,18,0.14)' : 'none',
                  '&:active, &.Mui-focusVisible': { bgcolor: 'rgba(20,20,18,0.08)' },
                  '& svg': { fontSize: 21 },
                }}
              >
                {action.label}
                {action.icon}
              </ButtonBase>
            ))}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
